/* IotOsPlanResultsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;

const PagingOsPlanResultsFeed = require("./PagingOsPlanResultsFeed");
const Renderer = require("./IotOsPlanResultsViewRenderer");
const LinksColumn = require("./column_renderers/links/LinksComponent");
const DateColumn = require("./column_renderers/date/DateComponent");
const CreateComponent = require("../../../../lmrbcomponents/osplanexplorer/actions/create/CreateComponent");
const FileName = require("@gluenetworks/steve_utilities").fileName;
/**
 * @param {OsPlanEditor} gameEditor
 * @param {UserFeedbackComponent} modalUserFeedback
 */
class OsPlanResultsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        modalUserFeedback,
        steveIO,
        PlanEntity,
        UserSession
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._plan = undefined;
        this._planEntity = PlanEntity;
        this._executedDeviceIds = undefined;
        this._fileFactory = steveIO.fileFactory;
        this._file = steveIO.File;
        this._pagingExecutionResultsFeed = undefined;
        this._executionEntity = undefined;
        this._selectedPlanId = _.get(this._gameEditor._editorTabState, "launchOptions.planId");
        this._fileNamer = FileName;
        this._gridKey = "osPlan_results";
        this._activeOrgId = UserSession.activeOrganization.id;

        this._columnConfig = {
            deviceName: {
                label: "Device",
                priority: 1,
            },
            deviceSku: {
                label: "SKU",
                width: 250,
                priority: 2,
            },
            deviceIp: {
                label: "IP Address",
                width: 250,
                priority: 2,
            },
            status: {
                label: "Execution Status",
                priority: 4,
            },
            progress: {
                label: "Phase",
                priority: 5,
            },
            currentPhase: {
                label: "Message",
                priority: 6,
            },
            percentComplete: {
                label: "% Complete",
                priority: 7,
            },
            startedOn: {
                label: "Started On",
                columnRendererComponent: DateColumn,
                priority: 8,
            },
            completedOn: {
                label: "Completed On",
                columnRendererComponent: DateColumn,
                priority: 9,
            },
            Log: {
                label: "Log",
                priority: 10,
                entityColumn: true,
                columnRendererComponent: LinksColumn,
                context: {
                    gameEditor: this._gameEditor,
                },
            },
        };

        this._renderer = new Renderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _patchSorting(sorting) {
        return sorting;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        this._plan.on(
            "PristinePropertiesChanged",
            (this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this))
        );
    }

    _unregisterListeners() {
        this._plan.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _unregisterGridListeners() {
        if (!_.isEmpty(this._pagingExecutionResultsFeed)) {
            // this._pagingExecutionResultsFeed._feed.removeAllListeners("Page");
            // this._pagingExecutionResultsFeed._feed.removeAllListeners("Repage");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("UpdateEventHandled");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("ChangeFeedInitialized");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("ChangeFeedStarted");
            this._pagingExecutionResultsFeed._feed.removeAllListeners("EntityPropertiesUpdated");
            this._removeUpdateHandlers();
        }
    }

    _removeUpdateHandlers() {
        _.forEach(_.get(this._pagingExecutionResultsFeed._feed, "cache", []), (resultEntity) => {
            resultEntity.removeAllListeners("StatusPropertyUpdated");
        });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get executionEntityData() {
        return this.getExecutionEntityData();
    }

    getExecutionEntityData() {
        return this._executionEntity && this._executionEntity.getPristineProperties();
    }

    get gridKey() {
        return this.getGridKey();
    }

    getGridKey() {
        return this._gridKey;
    }

    get gameEditorRootElement() {
        return this._gameEditor.renderer._rootElem;
    }

    shutdown() {
        this._gameEditor.clearViewActions();
        this._unregisterListeners();

        return Promise.resolve()
            .then(() => this._renderer.shutdown())
            .then(
                () => {
                    this._logger = null;
                    this._gameEditor = null;
                    this._userSession = null;
                    this._modalUserFeedback = null;
                    this._selectedPlanId = null;
                    this._plan = null;
                    this._pagingExecutionResultsFeed = null;
                    this._executionEntity = null;
                    this._gridKey = null;
                    this._columnConfig = null;
                    this._renderer = null;
                    this._executedDeviceIds = null;

                    Promise.resolve();
                },
                (err) => {
                    this._logger.warn({error: err}, "OsPlanResultsViewComponent.shutdown() error.");
                }
            );
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenPlanIsReady()
            .then(() => {
                this._plan = this._gameEditor.getPlan();
                this._registerListeners();
                return this._getExecutionEntity();
            })
            .then((executionEntityFeed) => {
                this._executionEntity = Object.values(executionEntityFeed.cache)[0];
                if (this._executionEntity && this._executionEntity.status === "RUNNING") {
                    this._gameEditor._runWorkId = _.get(this._executionEntity, "workId", undefined);
                }
                return this._executionEntity;
            })
            .then((_executionEntity) => {
                if (_executionEntity) {
                    this._hideLoader();
                    return this._getResultsFeed(_executionEntity);
                } else {
                    if (this._gameEditor._workId) {
                        this._showLoader();
                    }
                }
            })
            .catch((error) => {
                if (error !== "no Execution" && error.error !== "no Execution") {
                    this._logger.warn({error}, "Plan Execution Results View model error.");
                }
            });
    }

    _getResultsFeed(_executionEntity) {
        this.emit("ShowCancelAction");
        return this._getExecutionResultsFeed(_executionEntity);
    }

    _getExecutionResultsFeed(executionEntity) {
        return Promise.resolve(executionEntity)
            .then((executionEntity) => {
                if (!executionEntity) {
                    return Promise.reject("no Execution");
                }
                const userSorting = {};
                const patchedSort = this._patchSorting(userSorting);
                return this._getPagingFeed(patchedSort);
            })
            .then(() => {
                this._hideLoader();
                this._setQueryDeviceIds(this._pagingExecutionResultsFeed._feed);
                // TODO : Needs cleaning
                this._gameEditor._pagingExecutionFeed = _.values(
                    this._pagingExecutionResultsFeed._feed._cache
                );
                if (_.isEmpty(this._gameEditor._runWorkId)) {
                    this._unregisterGridListeners();
                }

                this.emit("EnableDisableExecutionButtons");
                return Promise.resolve();
            })
            .catch((error) => {
                if (error !== "no Execution" && error.error !== "no Execution") {
                    this._logger.warn({error}, "Plan Execution Results View model error.");
                }
            });
    }

    _getPagingFeed(patchedSort) {
        this._pagingExecutionResultsFeed = new PagingOsPlanResultsFeed({
            feedKey: this._gridKey + "_feed",
            userSession: this._userSession,
            entityDef: this._executionEntity,
            columnConfig: this._columnConfig,
            additionalDataFields: ["osmOptionJTypes", "osmOptions"],
            sort: patchedSort,
        });
        return this.whenFeedIsReady();
    }

    _showLoader() {
        this._modalUserFeedback.showLoadingProgress();
    }
    _hideLoader() {
        this._modalUserFeedback.hideDisplayed();
    }

    whenFeedIsReady() {
        return this._pagingExecutionResultsFeed.whenFeedReady().catch((err) => {
            this._logger.warn({error: err}, "OsPlanResultsViewComponent.whenFeedReady() error.");
        });
    }
    _setQueryDeviceIds(feed) {
        if (!_.isEmpty(feed._cache)) {
            this._executedDeviceIds = Object.keys(feed._cache);
        }
    }
    onUserSorted(sortData) {
        sortData = [].concat(sortData);
        let sort = {};
        sortData.forEach((sortData) => {
            if (!sortData || !sortData.sortColumn || !this._columnConfig[sortData.sortColumn]) {
                return;
            }
            const sortCriteria =
                this._columnConfig[sortData.sortColumn].valuePath ||
                this._columnConfig[sortData.sortColumn].path ||
                sortData.sortColumn;

            sort[sortCriteria] = sortData.sort === "asc" ? 1 : -1;
        });
        this._pagingExecutionResultsFeed.setSort(this._patchSorting(sort));
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged(patch) {
        if (!_.isEmpty(patch) && !this._gameEditor._planDeleted) {
            if (_.includes(["ERROR", "COMPLETE", "CANCELED"], patch.status)) {
                if (!_.isEmpty(this._gameEditor._runWorkId)) {
                    this.updateFeed().then(() => {
                        this.emit("UpdateGrid");
                    });
                    this._gameEditor._runWorkId = undefined;
                } else {
                    this.emit("Render");
                }
                this._unregisterGridListeners();
            }
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /**
     * Gets Execution Entity, if it is not provided
     * @returns {Promise<ConfigAuditExecutionEntity | Error>}
     * @private
     */

    _getExecutionEntity() {
        //return this._plan.loadRelatedExecution({});
        this._pagingExecutionEntityFeed = new PagingOsPlanResultsFeed({
            feedKey: this._gridKey + "_execution_entity_feed",
            entityDef: this._plan,
        });
        return this._pagingExecutionEntityFeed.whenFeedReady();
    }

    updateFeed() {
        if (
            this._pagingExecutionResultsFeed &&
            this._pagingExecutionResultsFeed.shutdown &&
            this._renderer._planResultsGrid
        ) {
            return this._pagingExecutionResultsFeed
                .shutdown()
                .then(() => {
                    return this._executionEntity.stopRelationshipFeed("results");
                })
                .then(() => {
                    this._pagingExecutionResultsFeed = undefined;

                    const userSorting = {};
                    const patchedSort = this._patchSorting(userSorting);
                    return this._getPagingFeed(patchedSort);
                })
                .then(() => {
                    if (!_.isEmpty(this._renderer._planResultsGrid)) {
                        return this._renderer._planResultsGrid.shutdown();
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    this._renderer._planResultsGrid = undefined;
                });
        }
        return Promise.resolve();
    }

    _cancelRemaining() {
        return Promise.resolve()
            .then(() => {
                return this._plan.cancelPlanWork(this._gameEditor._runWorkId);
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OsPlanResultsViewComponent._cancelRemaining() error."
                );
                return Promise.reject();
            });
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    async _createPlanFromError() {
        //looking for -v<number> at end of name and increment
        const regexVersion = /^(.*)-v([0-9]+)$/;
        let newName = _.get(this._plan, "name", "unknown");
        const results = newName.match(regexVersion);

        if (results && results[2]) {
            newName = results[1] + "-v" + (parseInt(results[2]) + 1);
        } else {
            newName = newName + "-v1";
        }

        // now check for duplicated name, if so, keep 'incrementing' like we do on clone
        let unique;
        do {
            const exist = await this._planEntity.retrieveOsUpgradePlanByName(
                this._activeOrgId,
                newName,
                {id: true}
            );
            unique = true;
            if (exist) {
                newName = newName + "-1";
                unique = false;
            }
        } while (!unique);

        return this._modalUserFeedback
            .displayCustom(
                new CreateComponent(
                    this._logger,
                    "Please specify a name for this cloned plan.",
                    "",
                    newName
                ),
                {dontHide: true}
            )
            .then((result) => {
                return this._modalUserFeedback.displayLoading().then(() => {
                    return this._plan.cloneErrors(result);
                });
            })
            .then((result) => {
                this._gameEditor.launchNewTab(result);
            })
            .then(() => {
                return this._modalUserFeedback.hideDisplayed();
            })
            .catch((err) => {
                if (err.userRejected) {
                    this._modalUserFeedback.hideDisplayed();
                } else {
                    this._logger.warn(
                        {error: err},
                        "OsPlanResultsViewComponent._createPlanFromError() - Error"
                    );
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    _downloadWorkResults() {
        return this._plan
            .runPlanReportWork()
            .then((result) => {
                let fileName = this._fileNamer({
                    solution: "OSM",
                    general: "PlanExecReport",
                    specific: this._plan.pristineProperties.name,
                    suffix: "csv",
                });
                let file = new this._file(fileName, null, result);
                this._fileFactory.saveFile(file);
                return Promise.resolve();
            })
            .catch((error) => this._logger.warn({error}, "Plan onDownloadWorkResults - error"));
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = OsPlanResultsViewComponent;

OsPlanResultsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.entities.OdmOsUpgradePlan",
    "$teve.userSession",
];

OsPlanResultsViewComponent.$displayDetails = {
    type: "View",
    name: "OS Plan Execution",
    icon: "common.results_link",
    shown: true,
    title: "OS Plan Execution",
    order: 5,
};
